import {
  Blockquote,
  Grid,
  Group,
  Modal,
  Badge,
  Stack,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { showNotification } from "@mantine/notifications"
import {
  IconAlertCircle,
  IconArrowRight,
  IconAt,
  IconCheck,
  IconCopy,
  IconUser,
} from "@tabler/icons-react"
import { Button, Card, DateCell, Select } from "@tfg/ui"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { NotificationsResponses } from "@tfg/types/endpoints/notifications"

import { i18n } from "~/i18n.ts"
import { useMarkNotificationArchivedMutation } from "~/pages/Brand/Home/hooks/useMarkNotificationArchivedMutation.ts"

interface Props {
  notification: NotificationsResponses.Notification
}

export const EmailNotification = ({ notification }: Props) => {
  const MESSAGES: Record<
    Props["notification"]["messageType"],
    { cause: string; action: string }
  > = {
    TRACEABILITY_DELIVERABILITY: {
      cause: i18n.t("home:notifications.traceability.deliverability.cause"),
      action: i18n.t("home:notifications.traceability.deliverability.action"),
    },
    TRACEABILITY_UNANSWERED: {
      cause: i18n.t("home:notifications.traceability.unanswered.cause"),
      action: i18n.t("home:notifications.traceability.unanswered.action"),
    },
  }

  const { t } = useTranslation(["home"])
  const [opened, { open, close }] = useDisclosure(false)
  const [language, setLanguage] = useState<string>(i18n.language)

  const messages: Record<Props["notification"]["messageType"], string> = {
    TRACEABILITY_DELIVERABILITY: i18n.t(
      "home:notifications.copy.messages.undelivery",
      { lng: language }
    ),
    TRACEABILITY_UNANSWERED: i18n.t(
      "home:notifications.copy.messages.unanswered",
      { lng: language }
    ),
  }

  const { mutate: markAsDone, isPending: isLoading } =
    useMarkNotificationArchivedMutation()

  const archiveNotification = (id: number) => {
    markAsDone(id)
    showNotification({
      color: "primary",
      message: t("notifications.archived.success"),
    })
  }

  const copy = () => {
    const finalMessage = messages[notification.messageType]
      .replaceAll("<eol/>", "\n")
      .replaceAll("</bold>", "")
      .replaceAll("<bold>", "")
    navigator.clipboard.writeText(finalMessage)
    showNotification({
      color: "primary",
      message: t("notifications.copy.success"),
    })
    close()
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title={t("notifications.copy.title")}
        size="55%"
        centered
      >
        <Stack>
          <Group justify="right">
            <Select
              data={[
                { value: "fr", label: t("notifications.language.french") },
                { value: "en", label: t("notifications.language.english") },
              ]}
              defaultValue={language}
              onChange={(value) => {
                setLanguage(value || i18n.language)
              }}
            />
            <Button
              icon={<IconCopy />}
              onClick={() => copy()}
              variant="secondary"
            >
              {t("notifications.actions.copy")}
            </Button>
          </Group>
          <Blockquote
            cite={notification.contact.email}
            style={{ fontSize: "0.85rem" }}
          >
            <Trans
              i18nKey={
                notification.messageType === "TRACEABILITY_UNANSWERED"
                  ? "home:notifications.copy.messages.unanswered"
                  : "home:notifications.copy.messages.undelivery"
              }
              t={t}
              components={{ eol: <br />, bold: <strong /> }}
              tOptions={{
                lng: language,
              }}
            />
          </Blockquote>
        </Stack>
      </Modal>
      <Card p="sm" bg="white">
        <Stack>
          <Group justify="apart">
            <Group wrap="nowrap">
              <Badge variant="info">
                {t("notifications.traceability.type")}
              </Badge>
              <DateCell date={notification.date} />
            </Group>
          </Group>
          <Grid>
            <Grid.Col span={4}>
              <Stack gap={5}>
                <Group wrap="nowrap" gap="xs">
                  <IconUser size={16} color="var(--icon-info-color)" />
                  <Text size="sm">{notification.contact.name}</Text>
                </Group>{" "}
                <Group wrap="nowrap" gap="xs">
                  <IconAt size={16} color="var(--tag-color-info)" />
                  <Text>{notification.contact.email}</Text>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col
              span={8}
              style={{
                display: "flex",
                alignItems: "flex-end",
                flexDirection: "column",
              }}
            >
              <Stack gap={5}>
                <Group gap="xs" wrap="nowrap">
                  <IconAlertCircle size={16} color="var(--icon-info-color)" />
                  <Text>{MESSAGES[notification.messageType].cause}</Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <IconArrowRight size={16} color="var(--tag-color-info)" />
                  <Text>{MESSAGES[notification.messageType].action}</Text>
                </Group>
              </Stack>
            </Grid.Col>
          </Grid>
          <Group w="100%" grow>
            {notification.archivedAt === null && (
              <>
                <Button
                  icon={<IconCopy />}
                  loading={isLoading}
                  variant="secondary"
                  fullWidth={true}
                  onClick={() => open()}
                  style={{ flexGrow: 1, flexBasis: 0 }}
                >
                  {t("notifications.actions.copy")}
                </Button>
                <Button
                  icon={<IconCheck />}
                  loading={isLoading}
                  variant="primary"
                  fullWidth={true}
                  style={{ flexGrow: 1, flexBasis: 0 }}
                  onClick={() => archiveNotification(notification.id)}
                >
                  {t("notifications.actions.markAsDone")}
                </Button>
              </>
            )}
          </Group>
        </Stack>
      </Card>
    </>
  )
}
